import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import humanizeString from 'humanize-string';
import { camelize } from 'humps';

import { formatCell } from '../../../utils';

import { AnswerTable, AssetActions, TermTranslations, SelectSpaceDropdown } from '../../molecules';
import { ContentEditable, Text, TimeAgo, NewButton, H4, H3, Icon } from '../../../../../components';
import { AssetFooterSource, AssetLoader } from '../../atoms';
import { useNotifications } from '../../../../../providers';
import { AssetCannedAnswer } from '../AssetCannedAnswer';
import {
  sourceShape,
  questionShape,
  spaceShape,
  termTranslationShape
} from '../../../../../propsShape';

const AssetTableViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  padding: 0 16px;
`;

const StyledText = styled(Text)`
  white-space: nowrap;
`;

const ResultsExplanation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AssetTableViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const AssetTableViewInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
`;

const AssetTableViewHeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SourcesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const ClickableIcon = styled(Icon)`
  cursor: pointer;
`;

const AssetTableView = ({
  question,
  termTranslations,
  spaces,
  navigate,
  onCreateSpace,
  onCreateQuestion,
  onAddQuestionToSpace,
  onRemoveQuestionFromSpace,
  answerSources,
  onColumnOrderChange = null,
  isReadOnly = false,
  onDeleteQuestion = null,
  onRetryQuestion = null,
  onAssetTitleChange = null
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { addNotification } = useNotifications();
  const gridRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    if (isEditing && titleRef.current) {
      titleRef.current.focus();
      // Move cursor to the end of the text
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(titleRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isEditing]);

  const handleFocus = useCallback(
    (evt) => {
      if (isReadOnly || !onAssetTitleChange) {
        evt.preventDefault();
        evt.target.blur();
      } else {
        setIsEditing(true);
      }
    },
    [setIsEditing]
  );

  const handleBlur = useCallback(
    (evt) => {
      setIsEditing(false);
      if (evt.target.innerText.trim() === '') {
        titleRef.current.innerText = question.assetTitle || question.body;
      }
      titleRef.current.innerText = evt.target.innerText.trim();
      onAssetTitleChange(question, titleRef.current.innerText);
    },
    [question]
  );

  const handleKeyDown = (evt) => {
    if (evt.key === 'Enter') {
      evt.target.blur();
    }
  };

  const appliedTermTranslations = useMemo(
    () =>
      termTranslations
        ?.filter((tt) => question.appliedTermTranslationIds?.includes(tt.id))
        .map((tt) => ({
          ...tt,
          termList: tt.termList.filter((term) =>
            question.body.toLowerCase().includes(term.toLowerCase())
          )
        })),
    [termTranslations, question]
  );

  const rowData = useMemo(
    () => question.answer?.data?.rows.map((r) => r.fields),
    [question.answer?.id, question.answer?.columnOrder]
  );

  // to sort percentage values
  const percentageComparator = (valueA, valueB) => {
    const parsePercentage = (value) => {
      if (typeof value === 'string') {
        return parseFloat(value.replace('%', '')) || 0;
      }
      return typeof value === 'number' ? value : 0;
    };

    const numA = parsePercentage(valueA);
    const numB = parsePercentage(valueB);

    return numA - numB;
  };

  const columnDefs = useMemo(
    () =>
      question.answer?.data?.headers.map((h) => ({
        field: h.column,
        headerName: humanizeString(h.column),
        cellRenderer: (params) => formatCell(params, question.answer.formattingHints),
        ...(['percentage needs multiplication', 'percentage no multiplication'].includes(
          question.answer.formattingHints?.[camelize(h.column)]
        ) && { comparator: percentageComparator })
      })),
    [question.answer?.id, question.answer?.formattingHints, question.answer?.columnOrder]
  );

  const handleColumnOrderChange = useMemo(() => {
    if (!onColumnOrderChange) return null;
    return (newColumnOrder) => onColumnOrderChange(newColumnOrder, question.answer?.id);
  }, [onColumnOrderChange, question?.answer?.id]);

  const onCopyTable = useCallback(
    (ref) => {
      if (!ref.current.api) return;

      const gridApi = ref.current.api;
      const selectedRows = gridApi.getSelectedRows();
      const dataToCopy = selectedRows.length > 0 ? selectedRows : rowData;

      // Get column headers
      const tableHeaders = columnDefs.map((col) => col.field);

      // Use tabs as separators
      let tsv = `${tableHeaders.join('\t')}\n`;
      dataToCopy.forEach((row) => {
        const sanitizedRow = Object.values(row).map((value) =>
          typeof value === 'string' ? value.replace(/\n/g, ' ') : value
        );
        tsv += `${sanitizedRow.join('\t')}\n`;
      });

      navigator.clipboard.writeText(tsv).then(() => {
        addNotification({
          type: 'info',
          icon: 'table-mode',
          message: 'Table data was copied to clipboard.'
        });
      });
    },
    [columnDefs, rowData, addNotification]
  );

  const onExportTable = useCallback(() => {
    if (!gridRef.current.api) return;

    const params = {
      skipHeader: false,
      fileName: `${question.exportFileName} - data.csv`,
      allColumns: true,
      onlySelected: false
    };

    const gridApi = gridRef.current.api;
    gridApi.exportDataAsCsv(params);
    addNotification({
      type: 'info',
      icon: 'table-mode',
      message: `Table data was saved as "${question.exportFileName} - data.csv"`
    });
  }, [gridRef.current, question.exportFileName]);

  const nonChartFollowUps = question.followUps.filter((followUp) => !followUp.isChart);

  // Show loading state when there's no answer or when status indicates processing
  if (!question.answer || question.status !== null) {
    return (
      <AssetTableViewContainer>
        <AssetLoader title={question.statusMessage || 'Processing answer...'} size={30} />
      </AssetTableViewContainer>
    );
  }

  if (question.answer?.isCanned) {
    return (
      <AssetTableViewContainer>
        <AssetCannedAnswer
          questionBody={question.body}
          answerBody={question.answer.body}
          rephrasedQuestions={question.rephrasedQuestions}
          onCreateQuestion={onCreateQuestion}
          isLastQuestion={nonChartFollowUps.size === 0}
          isCategoryError={question.answer.generatedBy === 'QuestionCategoryProcessor'}
          onDeleteQuestion={onDeleteQuestion ? () => onDeleteQuestion(question.id) : undefined}
          onRetryQuestion={onRetryQuestion ? () => onRetryQuestion(question) : undefined}
          questionId={question.id}
          actions={
            question.answer.generatedBy === 'LinkedSourceProcessor' && (
              <NewButton tertiary onClick={() => navigate('/organization/sources')}>
                Link source
              </NewButton>
            )
          }
        />
      </AssetTableViewContainer>
    );
  }

  return (
    <AssetTableViewContainer>
      <AssetTableViewHeader>
        <AssetTableViewInfo>
          <StyledText as='span' $color='var(--content-subtitle)'>
            <TimeAgo timestamp={question.createdAt} format='MMM D, YYYY [at] h:mm a' />
          </StyledText>
          <EditTitleContainer>
            <H4>
              {onAssetTitleChange ? (
                <ContentEditable
                  ref={titleRef}
                  $isEditing={isEditing}
                  contentEditable
                  suppressContentEditableWarning
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  disabled
                >
                  {question.assetTitle || question.body}
                </ContentEditable>
              ) : (
                question.assetTitle || question.body
              )}
            </H4>
            {onAssetTitleChange && (
              <ClickableIcon
                name='pencil'
                displaySize={16}
                color='var(--content-subtitle)'
                onClick={() => setIsEditing(true)}
              />
            )}
          </EditTitleContainer>
        </AssetTableViewInfo>
        <AssetTableViewHeaderActions>
          <AssetActions
            childQuestions={question.childQuestions}
            onCopyTable={() => onCopyTable(gridRef)}
            onDownload={onExportTable}
          />
          {!isReadOnly && (
            <SelectSpaceDropdown
              spaces={spaces}
              defaultSelectedSpaceIds={question.spaceIds?.filter((id) =>
                spaces?.some((space) => space.id === id)
              )}
              onAddQuestionToSpace={(space) => onAddQuestionToSpace(question, space)}
              onRemoveQuestionFromSpace={(space) => onRemoveQuestionFromSpace(question, space)}
              onCreateSpace={onCreateSpace}
            />
          )}
        </AssetTableViewHeaderActions>
      </AssetTableViewHeader>

      <H3>{question.answer.body}</H3>

      {question.answer?.rawAnswer && (
        <AnswerTable
          id={question.answer.id}
          formattingHints={question.answer.formattingHints}
          ref={gridRef}
          onColumnOrderChange={handleColumnOrderChange}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={rowData?.length > 100}
          pageSize={100}
          onCopyTable={onCopyTable}
        />
      )}
      {question.answer.queryExplanation && (
        <ResultsExplanation>
          <TermTranslations
            termTranslations={appliedTermTranslations}
            queryExplanation={question.answer.queryExplanation}
          />
          {answerSources?.length > 0 && (
            <SourcesContainer>
              {answerSources?.map((source, index) => (
                <AssetFooterSource
                  key={source.id}
                  source={source}
                  showPlus={index !== answerSources.length - 1}
                />
              ))}
            </SourcesContainer>
          )}
        </ResultsExplanation>
      )}
    </AssetTableViewContainer>
  );
};

AssetTableView.propTypes = {
  question: questionShape.isRequired,
  termTranslations: PropTypes.arrayOf(termTranslationShape).isRequired,
  spaces: PropTypes.arrayOf(spaceShape).isRequired,
  navigate: PropTypes.func.isRequired,
  onAssetTitleChange: PropTypes.func,
  onCreateSpace: PropTypes.func.isRequired,
  onColumnOrderChange: PropTypes.func,
  onCreateQuestion: PropTypes.func.isRequired,
  onDeleteQuestion: PropTypes.func,
  onRetryQuestion: PropTypes.func,
  onAddQuestionToSpace: PropTypes.func.isRequired,
  onRemoveQuestionFromSpace: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  answerSources: PropTypes.arrayOf(sourceShape).isRequired
};

export { AssetTableView };
